import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ProjectNote, ProjectNoteCategory } from '../../api/types';
import {
  useCreateProjectNote,
  useDeleteProjectNote,
  useProjectNotes,
  useUpdateProjectNote,
} from '../../hooks/useOps';
import { formatDate } from '../../lib/format';

type CategoryMeta = {
  value: ProjectNoteCategory;
  label: string;
  color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
};

const CATEGORIES: CategoryMeta[] = [
  { value: 'rule', label: 'Rule', color: 'primary' },
  { value: 'mistake', label: 'Mistake', color: 'error' },
  { value: 'important', label: 'Important', color: 'warning' },
  { value: 'future', label: 'Future idea', color: 'info' },
  { value: 'value', label: 'Value', color: 'success' },
  { value: 'general', label: 'General', color: 'default' },
];
const CATEGORY_META: Record<string, CategoryMeta> = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c]),
);

/** Today as YYYY-MM-DD in the browser's local timezone (so the <input type=date> agrees). */
function todayLocal(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
}

interface NoteDraft {
  noteId: string | null; // null = new
  title: string;
  category: ProjectNoteCategory;
  noteDate: string;
  body: string;
}

/**
 * The per-project "Daily Notes" tab: a categorized, date-stamped markdown
 * journal — rules we miss, mistakes to learn from, reminders for future
 * projects, notes on our value. Internal only (never client-visible).
 */
export function NotesTab({ projectId }: { projectId: string }) {
  const { data: notes, isLoading } = useProjectNotes(projectId);
  const create = useCreateProjectNote(projectId);
  const update = useUpdateProjectNote(projectId);
  const remove = useDeleteProjectNote(projectId);

  const [filter, setFilter] = useState<ProjectNoteCategory | 'all'>('all');
  const [draft, setDraft] = useState<NoteDraft | null>(null);
  const [editorView, setEditorView] = useState<'write' | 'preview'>('write');
  const [deleteNote, setDeleteNote] = useState<ProjectNote | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const visible = useMemo(
    () => (notes ?? []).filter((n) => filter === 'all' || n.category === filter),
    [notes, filter],
  );

  const openNew = () => {
    setEditorView('write');
    setDraft({ noteId: null, title: '', category: 'general', noteDate: todayLocal(), body: '' });
  };

  const openEdit = (n: ProjectNote) => {
    setEditorView('write');
    setDraft({ noteId: n.id, title: n.title, category: n.category, noteDate: n.noteDate, body: n.body });
  };

  const saving = create.isPending || update.isPending;

  const doSave = async () => {
    if (!draft || !draft.title.trim()) return;
    const body = {
      title: draft.title.trim(),
      category: draft.category,
      body: draft.body,
      noteDate: draft.noteDate,
    };
    try {
      if (draft.noteId) {
        await update.mutateAsync({ noteId: draft.noteId, body });
      } else {
        await create.mutateAsync(body);
      }
      setDraft(null);
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Save failed');
    }
  };

  if (isLoading) return <LinearProgress />;

  return (
    <Stack gap={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={2}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Daily Notes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Rules we miss, mistakes to learn from, reminders for future projects, notes on our value.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            New note
          </Button>
        </Stack>

        <ToggleButtonGroup
          size="small"
          value={filter}
          exclusive
          onChange={(_e, v) => v && setFilter(v)}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <ToggleButton value="all">All</ToggleButton>
          {CATEGORIES.map((c) => (
            <ToggleButton key={c.value} value={c.value}>
              {c.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {visible.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            {notes?.length
              ? 'No notes in this category.'
              : 'No notes yet. Capture a rule you keep missing, a mistake to learn from, or an idea for the next project.'}
          </Typography>
        ) : (
          <Stack gap={1.5}>
            {visible.map((n) => {
              const meta = CATEGORY_META[n.category];
              return (
                <Paper key={n.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" gap={1} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mb={0.5}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={meta?.color ?? 'default'}
                          label={meta?.label ?? n.category}
                        />
                        <Typography variant="subtitle2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                          {n.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          · {formatDate(n.noteDate)}
                        </Typography>
                      </Stack>
                      {n.body.trim() ? (
                        <Box
                          sx={{
                            '& p': { my: 0.5 },
                            '& ul, & ol': { my: 0.5, pl: 3 },
                            '& h1, & h2, & h3': { mt: 1, mb: 0.5 },
                            '& pre': {
                              bgcolor: 'action.hover',
                              p: 1,
                              borderRadius: 1,
                              overflow: 'auto',
                            },
                            '& code': { fontFamily: 'monospace' },
                            '& table': { borderCollapse: 'collapse' },
                            '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5 },
                            fontSize: 14,
                          }}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{n.body}</ReactMarkdown>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          (no body)
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(n)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteNote(n)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* New / Edit dialog */}
      <Dialog open={Boolean(draft)} onClose={() => setDraft(null)} fullWidth maxWidth="md">
        <DialogTitle>{draft?.noteId ? 'Edit note' : 'New note'}</DialogTitle>
        <DialogContent>
          {draft && (
            <Stack gap={2} mt={1}>
              <TextField
                label="Title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                fullWidth
                autoFocus
                inputProps={{ maxLength: 255 }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={draft.category}
                    onChange={(e) =>
                      setDraft({ ...draft, category: e.target.value as ProjectNoteCategory })
                    }
                  >
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Date"
                  type="date"
                  value={draft.noteDate}
                  onChange={(e) => setDraft({ ...draft, noteDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />
              </Stack>

              <Box>
                <ToggleButtonGroup
                  size="small"
                  value={editorView}
                  exclusive
                  onChange={(_e, v) => v && setEditorView(v)}
                  sx={{ mb: 1 }}
                >
                  <ToggleButton value="write">Write</ToggleButton>
                  <ToggleButton value="preview">Preview</ToggleButton>
                </ToggleButtonGroup>
                {editorView === 'write' ? (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    <CodeMirror
                      value={draft.body}
                      height="300px"
                      extensions={[markdown()]}
                      onChange={(v) => setDraft({ ...draft, body: v })}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      minHeight: 300,
                      '& pre': { bgcolor: 'action.hover', p: 1, borderRadius: 1, overflow: 'auto' },
                      '& code': { fontFamily: 'monospace' },
                      '& table': { borderCollapse: 'collapse' },
                      '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5 },
                    }}
                  >
                    {draft.body.trim() ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.body}</ReactMarkdown>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        Nothing to preview yet.
                      </Typography>
                    )}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  Markdown supported — headings, lists, tables, checkboxes, code.
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={doSave}
            disabled={saving || !draft?.title.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteNote)} onClose={() => setDeleteNote(null)}>
        <DialogTitle>Delete note?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{deleteNote?.title}</strong> will be permanently removed. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteNote(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={async () => {
              if (!deleteNote) return;
              try {
                await remove.mutateAsync(deleteNote.id);
                setDeleteNote(null);
              } catch (e) {
                setErrorMsg((e as Error).message ?? 'Delete failed');
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
